// components/Mensalidades/PagamentoMensalModal.js
import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import styles from "./PagamentoMensal.module.css";
import { useNotification } from "../../hooks/useNotification";

const PagamentoMensalModal = ({ isOpen, onClose, onSave, mensalidade }) => {
  const initialState = {
    codigo_aluno: "",
    referencia: "",
    valor: 0,
    multa: 0,
    estado_multa: "nao_aplicada", // ativa, anulada, nao_aplicada
    forma_pagamento: null,
    detalhes: null,
  };

  const [paymentData, setPaymentData] = useState(initialState);
  const [loadingRef, setLoadingRef] = useState(false);
  const { notifyError, notifySuccess } = useNotification();

  // 🔹 1️⃣ Carregar dados ao abrir modal
  useEffect(() => {
    if (mensalidade && isOpen) {
      setPaymentData({
        codigo_aluno: mensalidade.codigo_aluno || "",
        referencia: mensalidade.referencia || "",
        valor: mensalidade.valor_pago || mensalidade.valor || 0,
        multa: Number(mensalidade.multa) || 0,
        estado_multa:
          mensalidade.estado_multa ||
          (mensalidade.multa > 0 ? "ativa" : "nao_aplicada"),
        forma_pagamento: mensalidade.forma_pagamento || null,
        detalhes: mensalidade.detalhes || null,
      });
    } else if (!mensalidade && isOpen) {
      setPaymentData(initialState);
    }
  }, [mensalidade, isOpen]);

  // 🔹 2️⃣ Buscar mensalidade por referência digitada
  const fetchMensalidadePorReferencia = async (referencia) => {
    if (!referencia || referencia.length < 5) return;

    setLoadingRef(true);

    try {
      const response = await fetch(
        `/api/mensalidades/referencia/${referencia}`
      );
      const data = await response.json();

      if (!response.ok) {
        setPaymentData((prev) => ({
          ...prev,
          multa: 0,
          estado_multa: "nao_aplicada",
          forma_pagamento: null,
          detalhes: null,
        }));
        return;
      }

      setPaymentData((prev) => ({
        ...prev,
        codigo_aluno: data.codigo_aluno || "",
        valor: data.valor || 0,
        multa: Number(data.multa) || 0,
        estado_multa: data.multa_anulada
          ? "anulada"
          : data.multa > 0
          ? "ativa"
          : "nao_aplicada",
        forma_pagamento: data.forma_pagamento || null,
        detalhes: data.detalhes || null,
      }));
    } catch (error) {
      console.error("Erro ao buscar mensalidade:", error);
      setPaymentData((prev) => ({
        ...prev,
        multa: 0,
        estado_multa: "nao_aplicada",
        forma_pagamento: null,
        detalhes: null,
      }));
    } finally {
      setLoadingRef(false);
    }
  };

  // 🔹 3️⃣ Observa mudanças na referência (debounce)
  useEffect(() => {
    if (!paymentData.referencia) return;
    const timer = setTimeout(() => {
      fetchMensalidadePorReferencia(paymentData.referencia.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [paymentData.referencia]);

  // 🔹 4️⃣ Registrar pagamento
  const handleCreatePayment = async () => {
    if (!paymentData.codigo_aluno || !paymentData.referencia) {
      notifyError("⚠️ Código do aluno e referência são obrigatórios.");
      return;
    }

    if (!paymentData.valor || Number(paymentData.valor) <= 0) {
      notifyError("⚠️ Informe um valor válido maior que zero.");
      return;
    }

    try {
      const response = await fetch("/mensalidades/pagamentos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          codigo_aluno: paymentData.codigo_aluno.trim(),
          referencia: paymentData.referencia.trim(),
          tipo_servico: "MENSALIDADE",
          valor: Number(Number(paymentData.valor).toFixed(2)),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        notifyError(data.message || "❌ Erro ao registrar pagamento.");
        return;
      }

      notifySuccess(data.message || "✅ Pagamento registrado com sucesso.");

      // 🔹 Atualiza modal com nova forma de pagamento e detalhes
      await fetchMensalidadePorReferencia(paymentData.referencia.trim());

      // 🔹 Notifica componente pai para atualizar tabela
      onSave && onSave();
    } catch (err) {
      console.error("Erro ao registrar pagamento:", err);
      notifyError("❌ Erro ao conectar ao servidor.");
    }
  };

  // 🔹 5️⃣ Anular multa
  const handleAnularMulta = async () => {
    if (paymentData.estado_multa !== "ativa") return;

    const confirmar = window.confirm(
      `Tem certeza que deseja anular a multa de ${paymentData.multa} MZN para o aluno ${paymentData.codigo_aluno}?`
    );
    if (!confirmar) return;

    try {
      const response = await fetch(`/api/mensalidades/anular-multa`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          codigo_aluno: paymentData.codigo_aluno.trim(),
          referencia: paymentData.referencia.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        notifyError(data.message || "❌ Erro ao anular a multa.");
        return;
      }

      notifySuccess(data.message || "✅ Multa anulada com sucesso.");

      // 🔹 Atualiza modal e tabela automaticamente
      await fetchMensalidadePorReferencia(paymentData.referencia.trim());
      onSave && onSave();
    } catch (err) {
      console.error("Erro ao anular multa:", err);
      notifyError("❌ Erro ao conectar ao servidor.");
    }
  };

  // 🔹 6️⃣ Fechar modal
  const handleCancel = () => {
    setPaymentData(initialState);
    onClose();
  };

  // 🔹 7️⃣ RENDER
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleCancel}
      className={styles.filterModal}
      overlayClassName={styles.modalOverlay}
    >
      <h2>Pagamento de Mensalidade</h2>

      <div className={styles.modalContent}>
        <label>
          Código do Aluno <span className={styles.required}>*</span>
          <input
            type="text"
            placeholder="Ex: 20260001"
            value={paymentData.codigo_aluno}
            onChange={(e) =>
              setPaymentData({ ...paymentData, codigo_aluno: e.target.value })
            }
            className={styles.shortInput}
          />
        </label>

        <label>
          Referência <span className={styles.required}>*</span>
          <input
            type="text"
            placeholder="Ex: 760226000109"
            value={paymentData.referencia}
            onChange={(e) =>
              setPaymentData({ ...paymentData, referencia: e.target.value })
            }
            className={styles.shortInput}
          />
          {loadingRef && <small>🔎 A verificar referência…</small>}
        </label>

        <label>
          Valor Pago (MZN) <span className={styles.required}>*</span>
          <input
            type="number"
            step="0.01"
            placeholder="Ex: 1300"
            value={paymentData.valor}
            onChange={(e) =>
              setPaymentData({ ...paymentData, valor: e.target.value })
            }
            className={styles.shortInput}
          />
        </label>

        {paymentData.estado_multa === "ativa" && (
          <div className={styles.multaContainer}>
            <p>
              <strong>Multa:</strong> {paymentData.multa} MZN
            </p>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleAnularMulta}
            >
              Anular Multa
            </button>
          </div>
        )}

        {paymentData.estado_multa === "anulada" && (
          <p>
            <strong>Multa:</strong> Anulada
          </p>
        )}

        {paymentData.estado_multa === "nao_aplicada" && (
          <p>
            <strong>Multa:</strong> Não aplicada
          </p>
        )}

        {paymentData.forma_pagamento && (
          <p>
            <strong>Forma de Pagamento:</strong> {paymentData.forma_pagamento}
          </p>
        )}

        {paymentData.detalhes && paymentData.detalhes.data && (
          <p>
            <strong>Detalhes:</strong>{" "}
            {new Date(paymentData.detalhes.data).toLocaleString()}{" "}
            {paymentData.detalhes.telefone &&
              `(Telefone: ${paymentData.detalhes.telefone})`}
          </p>
        )}

        <div className={styles.buttonsContainer}>
          <button className={styles.applyFilter} onClick={handleCreatePayment}>
            Registrar Pagamento
          </button>

          <button
            className={styles.cancelButton}
            onClick={handleCancel}
            type="button"
          >
            Cancelar
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PagamentoMensalModal;
